import { useCallback, useEffect, useMemo, useState } from 'react';

const storagePrefix = 'mgr:columns';
const anonymousUser = 'anonymous';

const getStorageKey = (tableId, userId) => tableId ? `${storagePrefix}:${userId || anonymousUser}:${tableId}` : null;

function normalizeColumns(columns) {
  return (columns || []).map(column => {
    const field = typeof column === 'string' ? column : column?.field || column?.name;
    if (!field) {
      return null;
    }

    return {
      field,
      hideable: typeof column === 'string' ? true : column.hideable !== false,
      defaultHidden: typeof column === 'string' ? false : column.defaultHidden === true,
    };
  }).filter(Boolean);
}

function getDefaultModel(columns) {
  return Object.fromEntries(
    columns
      .filter(column => column.hideable !== false && column.defaultHidden)
      .map(column => [column.field, false])
  );
}

function sanitizeModel(model, columns, defaultModel={}) {
  const columnMap = new Map(columns.map(column => [column.field, column]));
  return Object.fromEntries(
    Object.entries(model || {}).filter(([field, visible]) => {
      const column = columnMap.get(field);
      return column && column.hideable !== false && (
        visible === false || (visible === true && defaultModel[field] === false)
      );
    })
  );
}

function buildModel(model, columns) {
  const defaultModel = getDefaultModel(columns);
  return {
    ...defaultModel,
    ...sanitizeModel(model, columns, defaultModel),
  };
}

function readModel(storageKey, showOnceMigration) {
  if (!storageKey || typeof window === 'undefined') {
    return {};
  }

  try {
    const raw = window.localStorage.getItem(storageKey);
    const parsed = raw ? JSON.parse(raw) : {};
    const model = parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    return applyShowOnceMigration(storageKey, model, showOnceMigration);
  } catch (err) {
    console.warn('Failed to read column visibility:', err);
    return {};
  }
}

function writeModel(storageKey, model) {
  if (!storageKey || typeof window === 'undefined') {
    return;
  }

  try {
    if (Object.keys(model).length) {
      window.localStorage.setItem(storageKey, JSON.stringify(model));
    } else {
      window.localStorage.removeItem(storageKey);
    }
  } catch (err) {
    console.warn('Failed to save column visibility:', err);
  }
}

function applyShowOnceMigration(storageKey, model, migration) {
  if (!migration?.id || !Array.isArray(migration.fields) || typeof window === 'undefined') {
    return model;
  }

  const migrationKey = `${storageKey}:migration:${migration.id}`;
  if (window.localStorage.getItem(migrationKey)) {
    return model;
  }

  const next = {...model};
  migration.fields.forEach(field => {
    if (next[field] === false) {
      delete next[field];
    }
  });
  writeModel(storageKey, next);
  window.localStorage.setItem(migrationKey, '1');
  return next;
}

export function useColumnVisibility({ columns, tableId, userId, showOnceMigration }) {
  const normalizedColumns = useMemo(() => normalizeColumns(columns), [columns]);
  const storageKey = useMemo(() => getStorageKey(tableId, userId), [tableId, userId]);
  const [model, setModel] = useState(() => buildModel(readModel(storageKey, showOnceMigration), normalizedColumns));

  useEffect(() => {
    setModel(buildModel(readModel(storageKey, showOnceMigration), normalizedColumns));
  }, [normalizedColumns, showOnceMigration, storageKey]);

  const onColumnVisibilityModelChange = useCallback((nextModel) => {
    const defaultModel = getDefaultModel(normalizedColumns);
    const next = sanitizeModel(nextModel, normalizedColumns, defaultModel);
    writeModel(storageKey, next);
    setModel({...defaultModel, ...next});
  }, [normalizedColumns, storageKey]);

  return {
    columnVisibilityModel: model,
    onColumnVisibilityModelChange,
  };
}
